import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCustomerItem,
  updateCustomerItem,
  listMasterItems,
  addCatalogItemPhoto,
  removeCatalogItemPhoto,
} from '../../api/catalog';
import { listOrgCategoryNames } from '../../api/customers';
import { listSiteCategories } from '../../api/sites';
import { useCatalogItemPhotoUrl } from '../../hooks/useCatalogItemImageUrl';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { PhotoGallery } from '../../components/PhotoGallery';
import type { ChargeType, CustomerCatalogItem, CustomerSite, InvoiceFrequency } from '../../types/api';

interface Props {
  orgId: string;
  sites: CustomerSite[];
  open: boolean;
  onClose: () => void;
  item?: CustomerCatalogItem | null;
}

export function CatalogItemModal({ orgId, sites, open, onClose, item }: Props) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isEdit = !!item;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [masterCatalogItemId, setMasterCatalogItemId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [chargeTypeOverride, setChargeTypeOverride] = useState<'' | ChargeType>('');
  const [managedTermMonths, setManagedTermMonths] = useState('');
  const [managedFrequency, setManagedFrequency] = useState<'' | InvoiceFrequency>('');
  const [visible, setVisible] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const addPhotoMutation = useMutation({
    mutationFn: (file: File) => addCatalogItemPhoto(item!.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog', orgId] });
      showToast('Photo added.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not upload the photo.')),
  });

  const removePhotoMutation = useMutation({
    mutationFn: (photoId: string) => removeCatalogItemPhoto(item!.id, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog', orgId] });
      showToast('Photo removed.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not remove the photo.')),
  });

  const masterItemsQuery = useQuery({ queryKey: ['master-items'], queryFn: listMasterItems, enabled: open });

  // Category options depend on where the item is available: a specific
  // site offers that site's own list, "org-wide" offers the deduped union
  // across every site in the org (see api/customers.ts's listOrgCategoryNames).
  const siteCategoriesQuery = useQuery({
    queryKey: ['site-categories', siteId],
    queryFn: () => listSiteCategories(orgId, siteId),
    enabled: open && !!siteId,
  });
  const orgCategoriesQuery = useQuery({
    queryKey: ['org-category-names', orgId],
    queryFn: () => listOrgCategoryNames(orgId),
    enabled: open && !siteId,
  });
  const categoryOptions: string[] = siteId
    ? (siteCategoriesQuery.data ?? []).map((c) => c.name)
    : (orgCategoriesQuery.data ?? []);

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setDescription(item?.description ?? '');
    setCategory(item?.category ?? '');
    setPrice(item?.price ?? '');
    setMasterCatalogItemId(item?.masterCatalogItemId ?? '');
    setSiteId(item?.siteId ?? '');
    setChargeTypeOverride(item?.chargeTypeOverride ?? '');
    setManagedTermMonths(item?.managedTermMonths ? String(item.managedTermMonths) : '');
    setManagedFrequency(item?.managedFrequency ?? '');
    setVisible(item?.visible ?? true);
    setError(null);
  }, [open, item]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        description: description || undefined,
        category: category || undefined,
        price: Number(price),
        siteId: siteId || undefined,
        chargeTypeOverride: chargeTypeOverride === '' ? undefined : chargeTypeOverride,
        managedTermMonths: chargeTypeOverride === 'MANAGED' && managedTermMonths ? Number(managedTermMonths) : undefined,
        managedFrequency: chargeTypeOverride === 'MANAGED' && managedFrequency ? managedFrequency : undefined,
        visible,
      };
      if (isEdit) {
        return updateCustomerItem(item!.id, payload);
      }
      return createCustomerItem(orgId, { ...payload, masterCatalogItemId: masterCatalogItemId || undefined });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog', orgId] });
      showToast(isEdit ? 'Catalog item updated.' : 'Catalog item added.');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not save the catalog item.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !price) {
      setError('Name and price are required.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit catalog item' : 'New catalog item'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="catalog-item-form" type="submit" isLoading={mutation.isPending}>
            {isEdit ? 'Save' : 'Add item'}
          </Button>
        </>
      }
    >
      <form id="catalog-item-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Input label="Name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Photos</p>
          {isEdit ? (
            <PhotoGallery
              photos={item!.photos}
              usePhotoUrl={(photoId) => useCatalogItemPhotoUrl(item!.id, photoId)}
              onAdd={(file) => {
                setError(null);
                addPhotoMutation.mutate(file);
              }}
              onRemove={(photoId) => {
                setError(null);
                removePhotoMutation.mutate(photoId);
              }}
              isUploading={addPhotoMutation.isPending}
              isRemoving={removePhotoMutation.isPending}
            />
          ) : (
            <p className="text-xs text-slate-500">Save the item first, then edit it to add photos.</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            hint={siteId ? undefined : 'Org-wide items list categories from across all sites.'}
          >
            <option value="">No category</option>
            {categoryOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            {category && !categoryOptions.includes(category) && <option value={category}>{category}</option>}
          </Select>
          <Input
            label="Price (£)"
            type="number"
            step="0.01"
            min="0"
            required
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>

        {!isEdit && (
          <Select
            label="Based on master item (optional)"
            value={masterCatalogItemId}
            onChange={(e) => setMasterCatalogItemId(e.target.value)}
          >
            <option value="">None - custom item</option>
            {masterItemsQuery.data?.map((mi) => (
              <option key={mi.id} value={mi.id}>
                {mi.name}
              </option>
            ))}
          </Select>
        )}

        <Select label="Availability" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">Org-wide - every site</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} only
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Charge type override"
            value={chargeTypeOverride}
            onChange={(e) => setChargeTypeOverride(e.target.value as '' | ChargeType)}
          >
            <option value="">Use customer default</option>
            <option value="ONE_OFF">One-off</option>
            <option value="MANAGED">Managed</option>
          </Select>
          {chargeTypeOverride === 'MANAGED' && (
            <Input
              label="Term (months)"
              type="number"
              min="1"
              value={managedTermMonths}
              onChange={(e) => setManagedTermMonths(e.target.value)}
            />
          )}
        </div>

        {chargeTypeOverride === 'MANAGED' && (
          <Select
            label="Instalment frequency"
            value={managedFrequency}
            onChange={(e) => setManagedFrequency(e.target.value as '' | InvoiceFrequency)}
          >
            <option value="">Use customer default</option>
            <option value="MONTHLY">Monthly</option>
            <option value="ANNUAL">Annual</option>
          </Select>
        )}

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40"
          />
          Visible to customers (orderable now)
        </label>
      </form>
    </Modal>
  );
}
